import React, { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import { submitDocuments } from 'PrivateApis';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { useRetrieveConfirmationSale } from 'Utilities/hooks/useRetrieveConfirmationSale';
import { getLevel } from 'Utilities/sentry';

import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import { DOCUMENT_ERROR_TRIGGER, DOCUMENT_UPLOAD_TYPE, generateUploadDocumentAnalyticsEventString } from '../../DocumentCapture.helpers';
import LocalTexts from '../../DocumentCapture.texts.json';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureConfirmationProgressContextType, DocumentCaptureOfferContext } from '../../DocumentCaptureContexts';
import { captureSentryException } from '../../DocumentCaptureError/captureSentryException';
import { displayToast } from '../../DocumentCaptureError/displayToast';
import { DocumentCaptureError } from '../../DocumentCaptureError/DocumentCaptureError';
import DocumentCaptureButtons from '../../shared/DocumentCaptureButtons/DocumentCaptureButtons';
import { useImageManager } from '../hooks/useImageManager';

import { UploadDocumentSectionProps } from './UploadDocumentsSection.types';
import { UploadDocumentsTiles } from './UploadDocumentsTiles';

import styles from './UploadDocumentsSection.scss';

const LocalT = new MDText(LocalTexts);

export const PAGES_LIMIT = 20;

const UploadDocumentsSection = (
  { exitPath }: UploadDocumentSectionProps,
) => {
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();

  const {
    setProgress,
  } = useContext<DocumentCaptureConfirmationProgressContextType>(DocumentCaptureConfirmationProgressContext);

  const handleRetrieveConfirmationSaleSuccess = (data: DocumentCaptureConfirmationProgressContextType['progress']) => {
    if (setProgress) {
      setProgress(data);
    }
  };

  const handleRetrieveConfirmationSaleError = (err: unknown) => {
    displayToast(err);
    captureSentryException(err);
  };

  const { retrieveConfirmationSale } = useRetrieveConfirmationSale({
    onError: handleRetrieveConfirmationSaleError,
    onSuccess: handleRetrieveConfirmationSaleSuccess,
  });

  const {
    hasBlurryImages,
    hasRequestedReuploadActions,
    hasRequiredUploads,
    hasUploadedDocuments: hasUploadedDocumentsRaw,
    kind,
    sections,
    setShowErrorBox,
    uploadDocType,
  } = useImageManager();

  const context = useContext(DocumentCaptureOfferContext);
  const { valuation } = context;

  const enquiryId = valuation?.valuation_ref;

  const hasUploadedDocuments = hasUploadedDocumentsRaw;

  const docsEarlyFlow = uploadDocType === DOCUMENT_UPLOAD_TYPE.EARLY;

  const addLaterButtonText = docsEarlyFlow ? LocalT.translate('saveAndExit') : LocalT.translate('back');

  const continueButtonText = LocalT.translate('continue');

  const handleAddDocumentLaterClick = (analytics: string) => {
    if (submitting) {
      return;
    }

    const message = analytics;

    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_ADD_LATER_CLICKED(
      {
        buttonLabel: addLaterButtonText,
        label: generateUploadDocumentAnalyticsEventString({ hasRequestedReuploadActions, message, uploadDocType }),
      },
    );
  };

  const submitDocs = async () => {
    const hasBlurryOrReuploadRequest = hasRequestedReuploadActions || hasBlurryImages;
    const hasInvalidInputs = hasBlurryOrReuploadRequest || !hasRequiredUploads;

    if (hasInvalidInputs) {
      if (!hasBlurryOrReuploadRequest) {
        DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_NO_IMAGES_ON_SUBMISSION(
          {
            buttonLabel: continueButtonText as string,
            label: generateUploadDocumentAnalyticsEventString(
              { message: kind, uploadDocType },
            ),
          },
        );
      }

      setShowErrorBox(DOCUMENT_ERROR_TRIGGER.ON_SUBMISSION);
      window.scrollTo(0, 0);

      return;
    }

    if (enquiryId) {
      setSubmitting(true);

      try {
        await submitDocuments({ enquiryId: parseInt(enquiryId), kind });
        await retrieveConfirmationSale();

        const message = kind;

        DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_DOCUMENTS_SUBMITTED(
          {
            label: generateUploadDocumentAnalyticsEventString(
              { hasRequestedReuploadActions, message, uploadDocType },
            ),
          },
        );
        history.push(exitPath);
      } catch (err: unknown) {
        window?.Sentry?.captureException?.(new Error(err as string), {
          extra: { enquiryId, hasRequestedReuploadActions, kind, uploadDocType },
          level: getLevel(err as Error),
        });

        displayToast(new DocumentCaptureError(DocumentCaptureError.getErrorMessage(err)));
      } finally {
        // This is instant with client side blocking but with real BE latency this will give better feedback experience
        setSubmitting(false);
      }
    }
  };

  const loadingSpinnerWithText = (
    <div className={styles.spinnerContainer} data-testid='loadingspinner'>
      <LoadingArrow size="large" />
      <p>Processing your photos...</p>
    </div>
  );

  const showAddLaterButton = uploadDocType !== DOCUMENT_UPLOAD_TYPE.STANDALONE;

  return (
    <div className={hasUploadedDocuments ? styles.uploadDocumentsSection : styles.dropZoneContainer}>
      {submitting ? loadingSpinnerWithText : (
        <>
          <div className={cx(styles.tilesContainer, { [styles.visible]: hasUploadedDocuments })}>
            <UploadDocumentsTiles pagesLimit={PAGES_LIMIT} />
          </div>
          <DocumentCaptureButtons>
            <Button
              fullWidth
              data-cy='submitAndContinue'
              icon="chevron"
              label={continueButtonText}
              onClick={submitDocs}
            />
            {showAddLaterButton && <Button
              fullWidth
              as={Link}
              disabled={submitting}
              label={addLaterButtonText}
              onClick={(() => handleAddDocumentLaterClick(sections.analytics.homePage))}
              to={exitPath}
              variant='secondary'
            />}
          </DocumentCaptureButtons>
        </>
      )}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default UploadDocumentsSection;
